import { QueryClient, useMutation } from '@tanstack/react-query';
import { queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
// import { TChatPlatformIntegration } from '../../types/chat-platform.type';
import { z } from 'zod';
import { GET_LIST_INTEGRATION_LINE_QUERY_KEY, TGetListIntegrationLineResponse } from './get-list-integration-line.api';
import { TChannel } from '../../../_shared/types/channel.type';
// import { TChatPlatformIntegration } from '../../_shared/types/chat-platform.type';

/**
 * Delete Line Integration Validation Schema
 */
export const deleteLineIntegrationValidationSchema = z.object({
  organizationChannelId: z.string({ message: 'value must be string!' }),
});

export type TDeleteLineIntegrationValidation = z.infer<typeof deleteLineIntegrationValidationSchema>;
export type TDeleteLineIntegrationPayload = TDeleteLineIntegrationValidation;
export type TDeleteLineIntegrationResponse = ApiResponse<TChannel>;

export const deleteLineIntegration = async (
  payload: TDeleteLineIntegrationPayload
): Promise<TDeleteLineIntegrationResponse> => {
  const url = `/integration/line/org-channel/${payload.organizationChannelId}`;

  return axiosInstance.delete(url);
};

/**
 * Delete Line Integration React-Query Hook
 */
type UseDeleteLineIntegrationOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteLineIntegration, AxiosErrorCustom>;
};

export const useDeleteLineIntegration = ({ mutationConfig }: UseDeleteLineIntegrationOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteLineIntegration,
    ...restConfig,
  });
};

/**
 * Optimistic Update Delete Line Integration
 */
export const deleteLineIntegrationOptimistically = (queryClient: QueryClient, data: TDeleteLineIntegrationPayload) => {
  queryClient.setQueryData<TGetListIntegrationLineResponse>(
    [GET_LIST_INTEGRATION_LINE_QUERY_KEY],
    (oldData: TGetListIntegrationLineResponse | undefined) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        payload: {
          ...oldData.payload,
          rows: oldData.payload.rows.filter(
            (integration) => integration.organizationChannelId !== data.organizationChannelId
          ),
          count: oldData.payload.count - 1,
        },
      };
    }
  );
};

/**
 * Delete Line Integration React-Query Hook
 */
type UseDeleteLineIntegrationOptimisticallyOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteLineIntegration, AxiosErrorCustom>;
};

export const useDeleteLineIntegrationOptimistically = ({
  mutationConfig,
}: UseDeleteLineIntegrationOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation<TDeleteLineIntegrationResponse, AxiosErrorCustom, TDeleteLineIntegrationPayload>({
    mutationFn: deleteLineIntegration,
    onMutate: async (data: TDeleteLineIntegrationPayload) => {
      await queryClient.cancelQueries({ queryKey: [GET_LIST_INTEGRATION_LINE_QUERY_KEY] });

      const previousData = queryClient.getQueryData([GET_LIST_INTEGRATION_LINE_QUERY_KEY]);

      deleteLineIntegrationOptimistically(queryClient, data);

      return { previousData };
    },
    onError: (_err: unknown, _data: unknown, context: unknown) => {
      if (context && typeof context === 'object' && 'previousData' in context) {
        queryClient.setQueryData(
          [GET_LIST_INTEGRATION_LINE_QUERY_KEY],
          (context as { previousData: unknown }).previousData
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_LINE_QUERY_KEY] });
    },
    ...restConfig,
  });
};
